import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MainService } from '../main.service';
import { map, Observable } from 'rxjs';
import { PublicHoliday, SendoutStatusEnum } from '../../types';
import { formatISO, parseISO } from 'date-fns';

export enum CalendarDateAvailabilityReason {
  IsInThePast = 'IsInThePast',
  IsToday = 'IsToday',
  IsInTheNextTwoDays = 'IsInTheNextTwoDays',
  InsufficientApprovalTime = 'InsufficientApprovalTime',
  IsSunday = 'IsSunday',
  HasOtherSendoutTooClose = 'HasOtherSendoutTooClose',
  DenyOverride = 'DenyOverride',
  AllowOverride = 'AllowOverride',
  HasSendoutSameDay = 'HasSendoutSameDay',
}

export interface CalendarItem {
  id?: number;
  type: CalendarItemType;
  startDate: string;
  endDate: string;
  scope: string;
  title: string;
  description: string;
}

interface CalendarDateResponseModel {
  date: string;
  sendouts: CalendarDateSendout[];
  events: CalendarDateEvent[];
  calendarItems: CalendarItem[];
  allow: boolean;
  warn: boolean;
  reason: CalendarDateAvailabilityReason;
}

export interface CalendarDate {
  date: Date;
  sendouts: CalendarDateSendout[];
  events: CalendarDateEvent[];
  calendarItems: CalendarItem[];
  allow: boolean;
  warn: boolean;
  reason: CalendarDateAvailabilityReason;
}

export interface AdminCalendarResponseModel {
  dates: AdminCalendarResponseModelDate[];
}

export interface AdminCalendarResponseModelDate {
  date: string;
  calendarItems: CalendarItem[];
  workday: {
    isWorkingDay: boolean;
    reason: string;
    type: string;
  };
}

export interface AdminCalendarDate {
  date: Date;
  calendarItems: CalendarItem[];
  workday: {
    isWorkingDay: boolean;
    reason: string;
    type: string;
    publicHoliday?: PublicHoliday;
  };
}

export interface CalendarDateSendout {
  id: number;
  sendoutName: string;
  status: SendoutStatusEnum;
}

export interface CalendarDateEvent {
  id: string;
  eventName: string;
  eventUrl: string;
}

export enum CalendarItemType {
  Comment = 'Comment',
  HqNonWorkingDayExplicit = 'HqNonWorkingDayExplicit',
  HqNonWorkingDayImplicit = 'HqNonWorkingDayImplicit',
  HqWorkingDayExplicit = 'HqWorkingDayExplicit',
  DenyDate = 'DenyDate',
  AllowDate = 'AllowDate',
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(private http: HttpClient, private mainService: MainService) {}

  getCalendar(
    storeId: number,
    fromDate: Date,
    toDate: Date,
    sendoutId?: number
  ): Observable<CalendarDate[]> {
    return this.http
      .get<CalendarDateResponseModel[]>(
        this.mainService.getApiURL() +
          `calendar?store-id=${storeId}&from-date=${formatISO(fromDate, {
            representation: 'date',
          })}&to-date=${formatISO(toDate, {
            representation: 'date',
          })}&sendout-id=${sendoutId ?? ''}`,
        {
          headers: this.mainService.getHeaders(),
        }
      )
      .pipe(
        map((calendarDate) => {
          return calendarDate.map((date) => ({
            ...date,
            date: new Date(date.date + 'T12:00:00Z'),
          }));
        })
      );
  }

  getAdminCalendar(
    fromDate: Date,
    toDate: Date
  ): Observable<AdminCalendarDate[]> {
    return this.http
      .get<AdminCalendarResponseModel>(
        this.mainService.getApiURL() +
          `calendar/admin-calendar?from-date=${formatISO(fromDate, {
            representation: 'date',
          })}&to-date=${formatISO(toDate, { representation: 'date' })}`,
        {
          headers: this.mainService.getHeaders(),
        }
      )
      .pipe(
        map((response: AdminCalendarResponseModel) => {
          return response.dates.map((d) => {
            return {
              ...d,
              date: parseISO(d.date + 'T12:00:00Z'),
            };
          });
        })
      );
  }

  getAllItems(): Observable<CalendarItem[]> {
    return this.http.get<CalendarItem[]>(
      this.mainService.getApiURL() + 'calendar/items',
      {
        headers: this.mainService.getHeaders(),
      }
    );
  }

  createItem(date: CalendarItem): Observable<CalendarItem> {
    return this.http.post<CalendarItem>(
      this.mainService.getApiURL() + 'calendar/items',
      date,
      {
        headers: this.mainService.getHeaders(),
      }
    );
  }

  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(
      this.mainService.getApiURL() + `calendar/items/${id}`,
      {
        headers: this.mainService.getHeaders(),
      }
    );
  }
}
